import { useState, useEffect, useMemo } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { OnboardingStepHeader } from './OnboardingStepHeader';
import { OnboardingFooter } from './OnboardingFooter';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { useOrganization } from '@/lib/organization-context';
import { successToast, errorToast } from '@/components/ui/sonner';

interface AiCalibrationProps {
  onComplete: () => void;
  onSkip: () => void;
  onBack?: () => void;
}

const TONE_PREVIEWS: Record<string, { generic: string; contextual: string }> = {
  professional: {
    generic: 'Our team brings deep expertise and a proven track record in delivering enterprise-grade solutions that drive measurable business outcomes.',
    contextual: '{company} brings deep expertise and a proven track record in delivering {industry} solutions that drive measurable business outcomes for our clients.',
  },
  confident: {
    generic: 'We deliver results. Our track record speaks for itself — consistent execution, measurable impact, and solutions that outperform the competition.',
    contextual: '{company} delivers results across {industry}. Our track record speaks for itself — consistent execution, measurable impact, and solutions that outperform.',
  },
  consultative: {
    generic: "We understand the challenges you're facing and are committed to being a true partner in your success, bringing both expertise and genuine care to every engagement.",
    contextual: "{company} understands the unique challenges facing {industry} organisations and is committed to being a true partner, bringing both expertise and genuine care to every engagement.",
  },
  technical: {
    generic: 'Our architecture leverages containerized microservices with 99.99% uptime SLA, horizontal scaling capabilities, and comprehensive API documentation.',
    contextual: '{company} leverages proven architectures with 99.99% uptime SLA and comprehensive documentation, purpose-built for {industry} environments.',
  },
};

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional', description: 'Clear, structured, formal' },
  { value: 'confident', label: 'Confident', description: 'Direct, assertive, results-focused' },
  { value: 'consultative', label: 'Consultative', description: 'Collaborative, advisory, client-first' },
  { value: 'technical', label: 'Technical', description: 'Precise, detailed, specification-led' },
];

export function AiCalibration({ onComplete, onSkip }: Omit<AiCalibrationProps, 'onBack'>) {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [selectedTone, setSelectedTone] = useState('professional');
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [customPreview, setCustomPreview] = useState<string | null>(null);

  const previewText = useMemo(() => {
    if (customPreview) return customPreview;
    const templates = TONE_PREVIEWS[selectedTone] || TONE_PREVIEWS.professional;
    const rawName = organization?.name?.trim();
    const companyName = rawName || 'Your company';
    const industries = organization?.industry_focus as string[] | null;
    const industryLabel = industries?.length ? industries[0].toLowerCase() : null;
    if (industryLabel) {
      return templates.contextual.replace(/\{company\}/g, companyName).replace(/\{industry\}/g, industryLabel);
    }
    return templates.contextual.replace(/\{company\}/g, companyName).replace(/\s*\{industry\}\s*/g, ' ').replace(/\s{2,}/g, ' ');
  }, [selectedTone, customPreview, organization?.name, organization?.industry_focus]);

  useEffect(() => { setCustomPreview(null); }, [selectedTone]);

  const handleGeneratePreview = async () => {
    setIsPreviewLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('tone-preview', {
        body: {
          tone: selectedTone,
          companyName: organization?.name || undefined,
          industry: (organization?.industry_focus as string[] | null)?.[0] || undefined,
        },
      });
      if (error) throw error;
      if (data?.preview) setCustomPreview(data.preview);
    } catch (error) {
      console.error('Error generating preview:', error);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ default_tone_of_voice: selectedTone })
        .eq('id', user.id);
      if (error) throw error;
      successToast('AI calibrated', 'Your communication preferences have been saved.');
      onComplete();
    } catch (error) {
      console.error('Error updating preferences:', error);
      errorToast('Error', 'Failed to save preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <OnboardingStepHeader
        step={3}
        totalSteps={5}
        title="How should the AI write for you?"
        subtitle="Choose a tone that matches how your company communicates. You can change this anytime in Settings."
      />

      {/* Tone grid 2×2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {TONE_OPTIONS.map((tone) => {
          const isSelected = selectedTone === tone.value;
          return (
            <button
              key={tone.value}
              type="button"
              onClick={() => setSelectedTone(tone.value)}
              style={{
                padding: '12px 14px',
                border: `1px solid ${isSelected ? '#C2541A' : 'rgba(255,255,255,0.08)'}`,
                borderRadius: 8,
                background: isSelected ? 'rgba(194,84,26,0.06)' : 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 150ms',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 500, color: '#fff', marginBottom: 2 }}>
                {tone.label}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                {tone.description}
              </div>
            </button>
          );
        })}
      </div>

      {/* Preview box */}
      <div
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 6,
          padding: 14,
          marginTop: 16,
          position: 'relative',
        }}
      >
        <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
          <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(255,255,255,0.35)' }}>
            Preview
          </span>
          <button
            type="button"
            onClick={handleGeneratePreview}
            disabled={isPreviewLoading}
            className="flex items-center gap-1 transition-colors"
            style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <RefreshCw className={`w-3 h-3 ${isPreviewLoading ? 'animate-spin' : ''}`} />
            Regenerate
          </button>
        </div>
        {isPreviewLoading && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(10,13,20,0.6)', borderRadius: 6 }}>
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'rgba(255,255,255,0.4)' }} />
          </div>
        )}
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, fontStyle: 'italic' }}>
          "{previewText}"
        </p>
      </div>

      <OnboardingFooter onSkip={onSkip} skipLabel="Set tone later" loading={loading} onSubmit={handleSubmit} type="button" />
    </>
  );
}
